import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lightning, ArrowRight, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Join() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.get(`/invites/${code}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invite not found or expired"));
  }, [code]);

  const accept = async () => {
    if (!user) {
      // Save code + redirect to register
      localStorage.setItem("pending_invite_code", code);
      navigate("/register");
      return;
    }
    setAccepting(true);
    try {
      await api.post(`/invites/${code}/accept`);
      toast.success(`You're now training with ${invite.coach?.name}!`);
      localStorage.removeItem("pending_invite_code");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
    } finally {
      setAccepting(false);
    }
  };

  const coachAvatar = invite?.coach?.picture
    ? (invite.coach.picture.startsWith("/api/") ? `${BACKEND_URL}${invite.coach.picture}` : invite.coach.picture)
    : null;

  return (
    <div className="min-h-screen bg-surface-base flex items-center justify-center p-6 stripe-bg" data-testid="join-page">
      <div className="w-full max-w-lg tactical-card p-10 relative">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <img src="/icon-192.png" alt="Edge Training" className="h-10 w-10 rounded-lg" />
          <span className="font-heading text-lg tracking-tight leading-none text-white">EDGE TRAINING</span>
        </Link>

        {error && (
          <div className="text-center py-6" data-testid="invite-error">
            <h1 className="font-heading text-3xl mb-2">INVITE UNAVAILABLE</h1>
            <p className="text-sm text-text-secondary mb-6">{error}</p>
            <Link to="/"><button className="btn-kinetic px-6 py-3 text-xs label-tiny tracking-widest">HOME</button></Link>
          </div>
        )}

        {invite && (
          <div data-testid="invite-details">
            <div className="label-tiny text-brand-accent mb-2">// COACHING INVITATION</div>
            <h1 className="font-heading text-3xl sm:text-4xl mb-6">JOIN THE ROSTER.</h1>

            <div className="flex items-center gap-4 mb-6 border border-white/10 p-4">
              {coachAvatar ? (
                <img src={coachAvatar} alt="" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-brand-primary flex items-center justify-center font-heading text-xl">
                  {invite.coach?.name?.[0]?.toUpperCase() || <User size={24} />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="label-tiny mb-1">COACH</div>
                <div className="font-heading text-xl">{invite.coach?.name?.toUpperCase()}</div>
                {invite.coach?.coach_specialty && (
                  <div className="text-xs text-brand-accent mt-1">{invite.coach.coach_specialty}</div>
                )}
              </div>
            </div>

            {invite.note && (
              <div className="border-l-2 border-brand-accent pl-4 mb-6">
                <div className="label-tiny mb-1">FROM YOUR COACH</div>
                <p className="text-sm italic">"{invite.note}"</p>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6 label-tiny">
              <span>MODE: <span className="text-brand-accent">{invite.mode?.toUpperCase()}</span></span>
            </div>

            {loading ? (
              <div className="label-tiny text-text-secondary text-center">Loading...</div>
            ) : (
              <>
                {!user && (
                  <p className="text-xs text-text-secondary mb-4">You'll need a Edge Training account to accept.</p>
                )}
                <button
                  onClick={accept}
                  disabled={accepting}
                  className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="accept-invite-button"
                >
                  {accepting ? "JOINING..." : <>{user ? "ACCEPT INVITATION" : "SIGN UP & ACCEPT"} <ArrowRight size={14} weight="bold" /></>}
                </button>
                {user && (
                  <button onClick={() => navigate("/")} className="w-full mt-2 text-xs label-tiny tracking-widest text-text-secondary hover:text-white transition-colors py-2" data-testid="decline-invite-button">
                    DECLINE
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
